import { lazy, Suspense, useEffect, useState } from 'react'; // [MENU DEV] lazy + Suspense
import type { JSX, ReactNode } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { useUser } from '@taki/auth';
import {
  Button,
  cn,
  IconModule,
  IconStore,
  IconUser,
  NavItem,
  UserChip,
} from '@taki/ui';
// [MENU DEV] Second bloc d'imports @taki/ui, servant UNIQUEMENT le menu DEV
// (l'entree de rail et le fallback du Suspense). Groupe a part pour partir d'un
// seul geste : fondus dans le bloc ci-dessus, ils survivraient a la suppression
// sans que rien ne le signale (le typecheck ne traque pas les imports morts).
import { IconBrowserCode2, SettingsSection, SkeletonText } from '@taki/ui';
import { TakiWordmark } from './TakiWordmark.js';
import { AppsScreen } from './screens/AppsScreen.js';
import { AccountScreen } from './screens/AccountScreen.js';
import { AppStoreScreen } from './screens/AppStoreScreen.js';
import { useScrollShadows } from './useScrollShadows.js';
import type { ShellAppEntry } from './types.js';
// [MENU DEV] Bloc temporaire, suppression : packages/shell/src/dev/README.md
import { useVisibleAppEntries } from './dev/appVisibility.js';

/**
 * Le panneau Home est dessine a l'echelle de la maquette 1920 ; rendu tel quel
 * il est trop gros. Ratio valide par le fondateur sur son 15 pouces : 84 %.
 * Constante, jamais fluide : la vue du 15 pouces fait foi partout, et la
 * constance physique d'un ecran a l'autre est deja assuree par useDisplayScale
 * (qui corrige la racine rem, donc la boite du panneau avec).
 *
 * Les surfaces de travail (app plein ecran, hub Parametres) ne sont PAS
 * concernees : leur gabarit est deja en densite compacte, elles rendent a
 * 100 % de la taille de reference.
 */
const HOME_PANEL_SCALE = 0.84;

// [MENU DEV] Le menu DEV n'existe qu'en dev local. L'ecran est charge en
// import() DYNAMIQUE derriere le drapeau de Vite : au build de production
// import.meta.env.DEV vaut false, la branche disparait, et avec elle le
// chunk de l'ecran (verifie : aucun fichier emis pour ce module). Ni entree
// de rail, ni ecran, ni appel reseau en production. Ne JAMAIS remplacer
// import.meta.env.DEV par un helper importe, une variable intermediaire ou un
// optional chaining : c'est ce couple litteral + import() dynamique qui fait
// disparaitre la branche PUIS le chunk.
const DevScreen = import.meta.env.DEV ? lazy(() => import('./dev/DevScreen.js')) : null;

/** Sections du panneau Home (rail de gauche). */
export type HomeSection = 'apps' | 'account' | 'store';

// [MENU DEV] Section interne supplementaire. On n'elargit PAS le type public
// HomeSection : il est exporte par le package et pilote HOME_SECTION_LABELS
// dans TakiShell. Le menu DEV ne laisse donc aucune trace dans l'API du shell.
type HomeSectionInterne = HomeSection | 'dev';

/** Libelle editorial de chaque section (overline du contenu). */
const SECTION_LABELS: Record<HomeSection, string> = {
  apps: 'Apps',
  account: 'Mon compte',
  store: 'App Store',
};

// [MENU DEV] Le libelle du menu DEV se resout ICI, derriere le drapeau LITTERAL
// de Vite, et surtout PAS comme une cle de SECTION_LABELS : cet objet est
// retenu pour ses trois autres cles, donc esbuild ne peut pas en elaguer une
// propriete et la chaine 'DEV' partirait telle quelle dans le bundle de
// production. Sous cette forme, import.meta.env.DEV devient false au build et
// la branche disparait avec sa chaine.
function sectionLabelOf(section: HomeSectionInterne): string {
  return import.meta.env.DEV && section === 'dev'
    ? 'DEV'
    : SECTION_LABELS[section as HomeSection];
}

export interface HomePanelProps {
  /** Config runtime du client (branding, orgId...). */
  config: TakiConfig;
  /** Apps installees, dans l'ordre de `config.installedApps`. */
  appEntries: readonly ShellAppEntry[];
  /** Selection d'une app : le shell navigue (et ferme l'overlay le cas echeant). */
  onPickApp: (appId: string) => void;
  /** Ouvre le panneau Ask Taki AI. */
  onAskAI: () => void;
  /** Section affichee au montage (defaut "apps"). */
  initialSection?: HomeSection;
  /** Classes additionnelles sur la boite du panneau (ex. "relative z-10" en overlay). */
  className?: string;
}

/** Overline de section (ex: "APPS"), repere editorial discret. */
function Overline({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-300">
      {children}
    </span>
  );
}

/**
 * Panneau Home COMPLET (fenetre flottante de la maquette) : rail gauche (logo
 * du branding, nav Apps / Mon compte / App Store, bouton Ask Taki AI) +
 * contenu de la section courante (UserChip, ombres de scroll, mention
 * "powered by").
 *
 * La section est un ETAT INTERNE : changer de section ne navigue jamais. Le
 * meme panneau sert a DEUX endroits :
 *   (a) la route "/" (et /account, /store qui preselectionnent la section via
 *       `initialSection`), pose au centre du fond bg par le shell ;
 *   (b) l'overlay au-dessus d'une app (voile + flou fournis par le shell) :
 *       changer de section ne quitte PAS l'overlay, choisir une app =
 *       `onPickApp` (le shell navigue et ferme).
 */
export function HomePanel({
  config,
  appEntries,
  onPickApp,
  onAskAI,
  initialSection = 'apps',
  className,
}: HomePanelProps): JSX.Element {
  const { user, claims } = useUser();
  const [section, setSection] = useState<HomeSectionInterne>(initialSection); // [MENU DEV]
  const scrollShadows = useScrollShadows();

  // Un changement de route (/ -> /account) pendant que le panneau reste monte
  // doit re-preselectionner la section ; l'etat interne garde la main ensuite.
  useEffect(() => {
    setSection(initialSection);
  }, [initialSection]);

  const userName = user?.displayName ?? user?.email ?? 'Utilisateur';

  // [MENU DEV] Le masquage s'applique a la liste AFFICHEE ; la prop appEntries
  // reste la liste COMPLETE, dont l'ecran DEV a besoin pour recocher une app.
  const visibleApps = useVisibleAppEntries(appEntries);

  let content: ReactNode;
  if (section === 'account') {
    content = user != null ? <AccountScreen config={config} user={user} claims={claims} /> : null;
  } else if (section === 'store') {
    content = <AppStoreScreen />;
    // [MENU DEV] Branche a retirer en entier, avec son Suspense.
  } else if (section === 'dev' && DevScreen != null) {
    content = (
      <Suspense
        fallback={
          <SettingsSection title="Branches et clones">
            <SkeletonText lines={3} />
          </SettingsSection>
        }
      >
        <DevScreen appEntries={appEntries} />
      </Suspense>
    );
  } else {
    // [MENU DEV] remettre `appEntries` a la place de `visibleApps` a la suppression.
    content = <AppsScreen apps={visibleApps} onPickApp={onPickApp} />;
  }

  return (
    <div
      className={cn(
        'flex h-[min(86vh,45rem)] w-full max-w-[75rem] overflow-hidden rounded-[1.625rem] border border-border bg-surface shadow-panel',
        className,
      )}
      style={{ zoom: HOME_PANEL_SCALE }}
    >
      {/* RAIL gauche */}
      <div className="flex w-[19.5rem] shrink-0 flex-col border-r border-border-soft p-[2.375rem_1.625rem_1.875rem]">
        <div className="mb-[2.375rem] px-2.5">
          {config.branding.logoUrl ? (
            /* 42 : hauteur du logo client dans la maquette (Spot Motion E2).
               Note : la hauteur juste depend de la forme du logo (un logo
               carre pese plus qu'un long lockup) ; a terme elle devrait venir
               du branding client. */
            <img
              src={config.branding.logoUrl}
              alt={config.branding.name}
              className="block h-[2.625rem] w-auto"
            />
          ) : (
            <span className="text-base font-semibold text-ink-900">
              {config.branding.name}
            </span>
          )}
        </div>

        <nav className="flex flex-col gap-2">
          <NavItem
            icon={<IconModule size={20} />}
            label="Apps"
            active={section === 'apps'}
            onClick={() => setSection('apps')}
          />
          <NavItem
            icon={<IconUser size={20} />}
            label="Mon compte"
            active={section === 'account'}
            onClick={() => setSection('account')}
          />
          <NavItem
            icon={<IconStore size={20} />}
            label="App Store"
            active={section === 'store'}
            onClick={() => setSection('store')}
          />
          {/* [MENU DEV] Entree temporaire. On teste DevScreen != null plutot que
              de reecrire le drapeau : une seule condition a maintenir, et
              l'entree ne peut pas exister sans son ecran. */}
          {DevScreen != null && (
            <NavItem
              icon={<IconBrowserCode2 size={20} />}
              label="DEV"
              active={section === 'dev'}
              onClick={() => setSection('dev')}
            />
          )}
        </nav>

        <div className="flex-1" />

        <Button variant="ai" size="lg" className="h-[3.375rem] w-full" onClick={onAskAI}>
          Ask Taki AI
        </Button>
      </div>

      {/* CONTENU droite. Le padding bas (5.5rem) reserve la bande blanche qui
          porte le "powered by" : il faut qu'elle respire autour du lockup,
          sinon le logo colle au bord du panneau. */}
      <div className="relative flex min-w-0 flex-1 flex-col p-[2.375rem_2.5rem_5.5rem]">
        <div className="mb-[1.625rem] flex items-center justify-between">
          <Overline>{sectionLabelOf(section)}</Overline>
          <UserChip
            name={userName}
            avatarUrl={user?.photoURL ?? undefined}
            onClick={() => setSection('account')}
          />
        </div>

        {/* Zone qui defile + son voile : le masque EFFACE le contenu au bord
            du cadre (rien n'est peint par-dessus), et chaque bord ne fond que
            s'il reste a faire defiler de ce cote. */}
        <div
          ref={scrollShadows.scrollRef}
          className={cn(
            'taki-scroll-mask min-h-0 flex-1 overflow-y-auto',
            scrollShadows.hasAbove && 'taki-scroll-mask-top',
            scrollShadows.hasBelow && 'taki-scroll-mask-bottom',
          )}
        >
          <div ref={scrollShadows.contentRef}>
            {/* Fondu discret rejoue a chaque changement de section (la cle
                remonte le contenu ; l'enveloppe mesuree, elle, ne bouge pas). */}
            <div key={section} className="taki-anim-fade">
              {content}
            </div>
          </div>
        </div>

        {/* Centre verticalement dans la bande de 5.5rem : 1.875rem dessous,
            autant au-dessus jusqu'a la zone de contenu. */}
        <div className="pointer-events-none absolute bottom-[1.875rem] right-8 flex items-center gap-2 opacity-50">
          <span className="text-[0.625rem] tracking-[0.04em] text-ink-500">powered by</span>
          {/* 29 : valeur de la maquette (Home, mention "powered by"). */}
          <TakiWordmark height={29} className="text-ink-500" />
        </div>
      </div>
    </div>
  );
}
